import type { TypeCode } from './types'

// ===== 行为倾向标签库 =====

const BEHAVIOR_TAGS = {
  // D1: 锚定来源
  A: [
    '独立判断，不随大流',
    '有自己的标准和节奏',
    '不太需要外界认可',
  ],
  R: [
    '重视他人反馈',
    '善于读懂环境信号',
    '喜欢和人交流想法',
  ],
  // D2: 处理模式
  S: [
    '喜欢有条理地做事',
    '会建立自己的方法论',
    '偏好结构化思考',
  ],
  C: [
    '灵活应变是强项',
    '讨厌一成不变',
    '随机应变能力强',
  ],
  // D3: 驱动来源
  I: [
    '做事看重意义感',
    '兴趣是最大动力',
    '内心有自己想做的事',
  ],
  E: [
    '目标明确有干劲',
    '喜欢看得见的成果',
    '外部认可是动力',
  ],
  // D4: 不确定性态度
  D: [
    '喜欢事情有定论',
    '讨厌悬而未决',
    '追求确定性',
  ],
  O: [
    '享受探索过程',
    '对未知保持好奇',
    '不急于下结论',
  ],
}

// ===== 类型配对关系 =====

interface TypeMatching {
  bestMatch: TypeCode    // 最佳拍档：仅 D/O 维度不同
  oppositeMatch: TypeCode // 欢喜冤家：四维度完全相反
}

const TYPE_MATCHING: Record<TypeCode, TypeMatching> = {
  ASID: { bestMatch: 'ASIO', oppositeMatch: 'RCEO' },
  ASIO: { bestMatch: 'ASID', oppositeMatch: 'RCED' },
  ASED: { bestMatch: 'ASEO', oppositeMatch: 'RCIO' },
  ASEO: { bestMatch: 'ASED', oppositeMatch: 'RCID' },
  ACID: { bestMatch: 'ACIO', oppositeMatch: 'RSEO' },
  ACIO: { bestMatch: 'ACID', oppositeMatch: 'RSED' },
  ACED: { bestMatch: 'ACEO', oppositeMatch: 'RSIO' },
  ACEO: { bestMatch: 'ACED', oppositeMatch: 'RSID' },
  RSID: { bestMatch: 'RSIO', oppositeMatch: 'ACEO' },
  RSIO: { bestMatch: 'RSID', oppositeMatch: 'ACED' },
  RSED: { bestMatch: 'RSEO', oppositeMatch: 'ACIO' },
  RSEO: { bestMatch: 'RSED', oppositeMatch: 'ACID' },
  RCID: { bestMatch: 'RCIO', oppositeMatch: 'ASEO' },
  RCIO: { bestMatch: 'RCID', oppositeMatch: 'ASED' },
  RCED: { bestMatch: 'RCEO', oppositeMatch: 'ASIO' },
  RCEO: { bestMatch: 'RCED', oppositeMatch: 'ASID' },
}

// ===== 每个类型的行为标签选择 =====
// 从四个维度中选取最有代表性的 3 条

const TYPE_BEHAVIOR_TAGS: Record<TypeCode, [string, string, string]> = {
  // A 开头的类型（内部锚定）
  ASID: [
    BEHAVIOR_TAGS.A[0],  // 独立判断，不随大流
    BEHAVIOR_TAGS.S[1],  // 会建立自己的方法论
    BEHAVIOR_TAGS.D[0],  // 喜欢事情有定论
  ],
  ASIO: [
    BEHAVIOR_TAGS.A[0],  // 独立判断，不随大流
    BEHAVIOR_TAGS.S[0],  // 喜欢有条理地做事
    BEHAVIOR_TAGS.O[0],  // 享受探索过程
  ],
  ASED: [
    BEHAVIOR_TAGS.A[1],  // 有自己的标准和节奏
    BEHAVIOR_TAGS.E[0],  // 目标明确有干劲
    BEHAVIOR_TAGS.D[2],  // 追求确定性
  ],
  ASEO: [
    BEHAVIOR_TAGS.A[1],  // 有自己的标准和节奏
    BEHAVIOR_TAGS.E[1],  // 喜欢看得见的成果
    BEHAVIOR_TAGS.O[1],  // 对未知保持好奇
  ],
  ACID: [
    BEHAVIOR_TAGS.A[0],  // 独立判断，不随大流
    BEHAVIOR_TAGS.C[0],  // 灵活应变是强项
    BEHAVIOR_TAGS.I[0],  // 做事看重意义感
  ],
  ACIO: [
    BEHAVIOR_TAGS.A[2],  // 不太需要外界认可
    BEHAVIOR_TAGS.C[0],  // 灵活应变是强项
    BEHAVIOR_TAGS.O[2],  // 不急于下结论
  ],
  ACED: [
    BEHAVIOR_TAGS.A[1],  // 有自己的标准和节奏
    BEHAVIOR_TAGS.C[2],  // 随机应变能力强
    BEHAVIOR_TAGS.E[0],  // 目标明确有干劲
  ],
  ACEO: [
    BEHAVIOR_TAGS.A[0],  // 独立判断，不随大流
    BEHAVIOR_TAGS.C[1],  // 讨厌一成不变
    BEHAVIOR_TAGS.O[0],  // 享受探索过程
  ],
  // R 开头的类型（外部锚定）
  RSID: [
    BEHAVIOR_TAGS.R[0],  // 重视他人反馈
    BEHAVIOR_TAGS.S[0],  // 喜欢有条理地做事
    BEHAVIOR_TAGS.I[1],  // 兴趣是最大动力
  ],
  RSIO: [
    BEHAVIOR_TAGS.R[2],  // 喜欢和人交流想法
    BEHAVIOR_TAGS.S[2],  // 偏好结构化思考
    BEHAVIOR_TAGS.O[1],  // 对未知保持好奇
  ],
  RSED: [
    BEHAVIOR_TAGS.R[0],  // 重视他人反馈
    BEHAVIOR_TAGS.S[1],  // 会建立自己的方法论
    BEHAVIOR_TAGS.E[1],  // 喜欢看得见的成果
  ],
  RSEO: [
    BEHAVIOR_TAGS.R[1],  // 善于读懂环境信号
    BEHAVIOR_TAGS.E[0],  // 目标明确有干劲
    BEHAVIOR_TAGS.O[0],  // 享受探索过程
  ],
  RCID: [
    BEHAVIOR_TAGS.R[2],  // 喜欢和人交流想法
    BEHAVIOR_TAGS.C[0],  // 灵活应变是强项
    BEHAVIOR_TAGS.I[2],  // 内心有自己想做的事
  ],
  RCIO: [
    BEHAVIOR_TAGS.R[1],  // 善于读懂环境信号
    BEHAVIOR_TAGS.C[2],  // 随机应变能力强
    BEHAVIOR_TAGS.O[2],  // 不急于下结论
  ],
  RCED: [
    BEHAVIOR_TAGS.R[0],  // 重视他人反馈
    BEHAVIOR_TAGS.C[1],  // 讨厌一成不变
    BEHAVIOR_TAGS.E[2],  // 外部认可是动力
  ],
  RCEO: [
    BEHAVIOR_TAGS.R[1],  // 善于读懂环境信号
    BEHAVIOR_TAGS.C[0],  // 灵活应变是强项
    BEHAVIOR_TAGS.O[0],  // 享受探索过程
  ],
}

// ===== 导出接口和函数 =====

export interface ShareCardContent {
  typeCode: TypeCode
  behaviorTags: [string, string, string]
  bestMatch: TypeCode
  oppositeMatch: TypeCode
}

export function getShareCardContent(typeCode: TypeCode): ShareCardContent {
  const matching = TYPE_MATCHING[typeCode]
  return {
    typeCode,
    behaviorTags: TYPE_BEHAVIOR_TAGS[typeCode],
    bestMatch: matching.bestMatch,
    oppositeMatch: matching.oppositeMatch,
  }
}

export { TYPE_MATCHING, TYPE_BEHAVIOR_TAGS }
